import React, { useState, useEffect, useRef } from "react";
import { FaUserCog, FaUserShield, FaUserFriends, FaUserAlt } from "react-icons/fa";
import axios from "axios";
import { motion } from "framer-motion";
import "./teamchart.css";

// Updated hierarchy order – CEO on top, new structure
const POSITION_HIERARCHY = [
  "CEO",
  "Directors",
  "Managers",
  "Team Leaders",
  "Assistant Team Leaders",
  "Associates",
];

const POSITION_ICONS = {
  CEO: <FaUserCog />,
  Directors: <FaUserCog />,
  Managers: <FaUserShield />,
  "Team Leaders": <FaUserFriends />,
  "Assistant Team Leaders": <FaUserFriends />,
  Associates: <FaUserAlt />,
};

const POSITION_COLORS = {
  CEO: "#8e44ad",
  Directors: "#1dd3b0",
  Managers: "#2ecc71",
  "Team Leaders": "#e67e22",
  "Assistant Team Leaders": "#f39c12",
  Associates: "#e74c3c",
};

const SINGULAR_TITLES = {
  CEO: "CEO",
  Directors: "Director",
  Managers: "Manager",
  "Team Leaders": "Team Leader",
  "Assistant Team Leaders": "Assistant Team Leader",
  Associates: "Associate",
};

const EmployeeHierarchy = () => {
  const [employeesByPosition, setEmployeesByPosition] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allEmployees, setAllEmployees] = useState([]);
  const [connections, setConnections] = useState([]);
  const containerRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        "https://backend.ems.hiradentallab.com/api/employees/hierarchy"
      );
      setEmployeesByPosition(response.data);
      const allEmps = Object.values(response.data).flat();
      setAllEmployees(allEmps);
      generateConnections(response.data, allEmps);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError(
        "Failed to load organizational hierarchy. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  const generateConnections = (employeesByPos, allEmps) => {
    const newConnections = [];
    allEmps.forEach((employee) => {
      if (employee.reportsTo) {
        const supervisor = allEmps.find(
          (e) => e._id === employee.reportsTo._id
        );
        if (supervisor) {
          newConnections.push({
            from: supervisor._id,
            to: employee._id,
            color: POSITION_COLORS[supervisor.position] || "#4a90e2",
          });
        }
      }
    });
    setConnections(newConnections);
  };

  if (loading) {
    return (
      <motion.div
        className="oh-loading"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="spinner"></div>
        Loading organizational hierarchy...
      </motion.div>
    );
  }

  if (error) {
    return (
      <motion.div
        className="oh-error-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="oh-error-text">{error}</div>
        <button className="oh-retry-button" onClick={fetchEmployees}>
          Retry
        </button>
      </motion.div>
    );
  }

  return (
    <div className="oh-container" ref={containerRef} style={{ marginTop: "3%" }}>
      <svg
        ref={svgRef}
        className="oh-connections-svg"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#4a90e2" />
          </marker>
        </defs>
        {connections.map((conn, idx) => {
          const fromEmployee = allEmployees.find((e) => e._id === conn.from);
          const toEmployee = allEmployees.find((e) => e._id === conn.to);
          if (!fromEmployee || !toEmployee) return null;
          return (
            <ConnectionLine
              key={`conn-${idx}`}
              fromId={fromEmployee._id}
              toId={toEmployee._id}
              containerRef={containerRef}
              color={conn.color}
            />
          );
        })}
      </svg>

      <motion.div
        className="oh-header"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="oh-header-text">Organizational Hierarchy</h1>
      </motion.div>

      <div className="oh-flow-container" style={{ position: "relative", zIndex: 2 }}>
        {POSITION_HIERARCHY.map((position, index) => (
          <PositionRow
            key={position}
            position={position}
            employees={employeesByPosition[position] || []}
            isFirst={index === 0}
            isLast={index === POSITION_HIERARCHY.length - 1}
            positionColor={POSITION_COLORS[position]}
          />
        ))}
      </div>
    </div>
  );
};

const PositionRow = ({ position, employees, isFirst, isLast, positionColor }) => {
  const shouldShowReportsTo = position !== "CEO";

  const employeeRows = [];
  for (let i = 0; i < employees.length; i += 5) {
    employeeRows.push(employees.slice(i, i + 5));
  }

  return (
    <motion.div
      className={`oh-role-section ${isFirst ? "oh-first-row" : ""}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div
        className="oh-heading"
        style={{ borderBottom: `3px solid ${positionColor}` }}
      >
        <span className="oh-role-icon">{POSITION_ICONS[position]}</span>
        {position}
      </div>
      <div className="oh-boxes-container">
        {employeeRows.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className="oh-boxes-row">
            {row.map((employee) => (
              <motion.div
                key={employee._id}
                className="oh-box"
                style={{ borderTop: `3px solid ${positionColor}` }}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
                data-employee-id={employee._id}
              >
                <div>
                  <div className="oh-employee-name">{employee.name}</div>
                  {/* Combined department + title badge */}
                  {employee.department && position !== "CEO" && position !== "Directors" && (
                    <div className="oh-department-badge">
                      {employee.department} {SINGULAR_TITLES[position] || position}
                    </div>
                  )}
                  {/* Optional designation override */}
                  {employee.designation && (
                    <div
                      style={{
                        backgroundColor: "#fff3e0",
                        color: "#e65100",
                        padding: "2px 10px",
                        borderRadius: "15px",
                        fontSize: "0.7rem",
                        textAlign: "center",
                        marginTop: 5,
                      }}
                    >
                      {employee.designation}
                    </div>
                  )}
                  {shouldShowReportsTo && employee.reportsTo && (
                    <div className="oh-reports-to">
                      Reports to: {employee.reportsTo.name}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        ))}
      </div>
      {!isLast && (
        <motion.div
          className="oh-vertical-connector"
          animate={{ height: [0, 40], opacity: [0, 1] }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />
      )}
    </motion.div>
  );
};

const ConnectionLine = ({ fromId, toId, containerRef, color }) => {
  const [path, setPath] = useState("");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const updatePath = () => {
      if (!containerRef.current) return;
      const fromElement = document.querySelector(
        `[data-employee-id="${fromId}"]`
      );
      const toElement = document.querySelector(
        `[data-employee-id="${toId}"]`
      );
      if (!fromElement || !toElement) {
        setVisible(false);
        return;
      }
      const containerRect = containerRef.current.getBoundingClientRect();
      const fromRect = fromElement.getBoundingClientRect();
      const toRect = toElement.getBoundingClientRect();
      const fromX = fromRect.left + fromRect.width / 2 - containerRect.left;
      const fromY = fromRect.bottom - containerRect.top;
      const toX = toRect.left + toRect.width / 2 - containerRect.left;
      const toY = toRect.top - containerRect.top;
      const controlY = fromY + (toY - fromY) * 0.3;
      const pathData = `M${fromX},${fromY} C${fromX},${controlY} ${toX},${controlY} ${toX},${toY}`;
      setPath(pathData);
      setVisible(true);
    };
    updatePath();
    const resizeObserver = new ResizeObserver(updatePath);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [fromId, toId, containerRef]);

  if (!visible || !path) return null;
  return (
    <path
      d={path}
      stroke={color}
      strokeWidth={2}
      fill="none"
      markerEnd="url(#arrowhead)"
    />
  );
};

export default EmployeeHierarchy;